import { supabase } from '../../lib/supabaseClient';
import type { Database } from '../../types/database.types';

export type DocumentTemplate = Database['public']['Tables']['document_templates']['Row'];
export type DocumentDraft = Database['public']['Tables']['document_drafts']['Row'];

export interface GenerateDocumentInput {
  dossierId: string;
  templateId: string;
  directives: string;
  contactIds: string[];
  draftId?: string;
  /** Remplit les {{ }} du modèle sans appeler de modèle de langage. */
  mergeOnly?: boolean;
}
export interface GenerateResult { draft_id: string; title: string; content_md: string }

export async function listTemplates(activeOnly: boolean): Promise<DocumentTemplate[]> {
  let q = supabase.from('document_templates').select('*')
    .order('display_order').order('name');
  if (activeOnly) q = q.eq('is_active', true);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function createTemplate(
  input: Pick<DocumentTemplate, 'name' | 'description' | 'category' | 'body_md' | 'instructions'> & { pdf_layout_id?: string | null },
): Promise<DocumentTemplate> {
  const { data, error } = await supabase.from('document_templates')
    .insert(input).select().single();
  if (error) throw error;
  return data;
}

export async function updateTemplate(
  id: string,
  patch: Partial<Pick<DocumentTemplate, 'name' | 'description' | 'category' | 'body_md' | 'instructions' | 'is_active' | 'display_order' | 'pdf_layout_id'>>,
): Promise<DocumentTemplate> {
  const { data, error } = await supabase.from('document_templates')
    .update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function listDossierDrafts(dossierId: string): Promise<DocumentDraft[]> {
  const { data, error } = await supabase.from('document_drafts').select('*')
    .eq('dossier_id', dossierId).eq('status', 'draft')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data ?? []; // la RLS scope déjà : auteur, ou tout si admin
}

export async function generateDocument(input: GenerateDocumentInput): Promise<GenerateResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Session expirée');
  const { data, error } = await supabase.functions.invoke('generate-document', {
    body: {
      dossier_id: input.dossierId,
      template_id: input.templateId,
      directives: input.directives,
      contact_ids: input.contactIds,
      draft_id: input.draftId,
      merge_only: input.mergeOnly === true,
    },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw new Error('La génération a échoué.');
  if (data && (data as { error?: string }).error) throw new Error(String((data as { error: string }).error));
  return data as GenerateResult;
}

export async function sendClientLetter(input: {
  dossierId: string;
  contactId: string;
  subject: string;
  filename: string;
  pdfBase64: string;
}): Promise<{ to: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Session expirée');
  const { data, error } = await supabase.functions.invoke('send-client-letter', {
    body: {
      dossier_id: input.dossierId,
      contact_id: input.contactId,
      subject: input.subject,
      filename: input.filename,
      pdf_base64: input.pdfBase64,
    },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw new Error('L’envoi a échoué.');
  const body = data as { error?: string; to?: string } | null;
  if (body?.error) throw new Error(body.error);
  if (!body?.to) throw new Error('L’envoi a échoué.');
  return { to: body.to };
}

export async function saveDraft(
  id: string,
  patch: { title?: string; content?: unknown },
): Promise<void> {
  const { error } = await supabase.from('document_drafts')
    .update({ ...patch, updated_at: new Date().toISOString() } as never).eq('id', id);
  if (error) throw error;
}

export async function discardDraft(id: string): Promise<void> {
  const { error } = await supabase.from('document_drafts')
    .update({ status: 'discarded', updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

export async function finalizeDraft(id: string, fileId: string): Promise<void> {
  const { error } = await supabase.from('document_drafts')
    .update({ status: 'finalized', finalized_file_id: fileId, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function findDraftByFinalizedFile(fileId: string): Promise<DocumentDraft | null> {
  const { data, error } = await supabase.from('document_drafts')
    .select('*')
    .eq('finalized_file_id', fileId)
    .neq('status', 'discarded')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function reopenDraft(id: string): Promise<void> {
  const { error } = await supabase.from('document_drafts')
    .update({ status: 'draft', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
